import { AdminPageHeader } from '@/components/patterns/admin-page'
import ProductForm from '@/components/forms/ProductForm'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { OrganizationType } from '@/lib/enums/OrganizationType'
import {
  findSubscriptionPackageCollection,
  subscriptionPackageCollectionFilters,
} from '@/lib/subscription-package-collection'
import { collectionQueries } from '@/lib/services/collections/collections.query'
import {
  locationFilters,
  locationQueries,
  useLocations,
} from '@/lib/services/locations/locations.query'
import { productFilters, productQueries, useProduct } from '@/lib/services/products/products.query'
import { productParamsSchema } from '@/lib/services/products/products.schema'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { Flower2 } from 'lucide-react'

const filters = productFilters().get()
const locationQueryFilters = () => locationFilters().get()

export const Route = createFileRoute('/_authenticated/$orgId/products/$productId')({
  component: ProductDetailPage,
  head: adminRouteTitleHead,
  params: {
    parse: (params) => productParamsSchema.parse(params),
  },
  loader: async ({ context, params }) => {
    if (!context.ability.can('view', 'products')) throw notFound()

    let product

    // If the user is global, then we need to know the product org before fetching org-scoped data.
    if (context.authState.user.organization?.type === OrganizationType.Global.Value) {
      product = await context.queryClient.ensureQueryData(
        productQueries.show({
          organizationId: params.orgId,
          productId: params.productId,
          filters,
        })
      )

      void context.queryClient.ensureQueryData(
        locationQueries.index({
          organizationId: product.organization.id,
          filters: locationQueryFilters(),
        })
      )
    } else {
      product = await context.queryClient.ensureQueryData(
        productQueries.show({
          organizationId: params.orgId,
          productId: params.productId,
          filters,
        })
      )

      void context.queryClient.ensureQueryData(
        locationQueries.index({
          organizationId: params.orgId,
          filters: locationQueryFilters(),
        })
      )
    }

    const productOrganizationId =
      product.organization?.id ?? product.organization_id ?? params.orgId
    const subscriptionCollection = context.ability.can('view', 'collections')
      ? findSubscriptionPackageCollection(
          await context.queryClient.ensureQueryData(
            collectionQueries.index({
              organizationId: productOrganizationId,
              filters: subscriptionPackageCollectionFilters,
            })
          )
        )
      : null

    return {
      product,
      isReadOnly: !context.ability.can('edit', 'products'),
      subscriptionCollection,
    }
  },
})

export function ProductDetailPage() {
  const { productId } = Route.useParams()
  const { subscriptionCollection } = Route.useLoaderData()
  const { data: product } = useProduct(productId, { filters })
  const { data: locations } = useLocations({
    organizationId: product.organization_id,
    filters: locationQueryFilters(),
  })
  const path = usePathHelper()
  const historyLink = path.products.link('$productId/history', productId)

  return (
    <div className="w-full max-w-none space-y-4">
      <AdminPageHeader icon={Flower2} title={product.name} />
      <ProductForm
        historyLink={historyLink}
        initialData={product}
        locations={locations}
        organizationId={product.organization_id}
        subscriptionCollection={subscriptionCollection}
      />
    </div>
  )
}
